import { retryWork, standingOn, takeBack } from './engine';
import type { Piece } from './pieces';
import { confirmAction } from '@/stores/confirm';
import { useShellNav } from '@/stores/shellNav';

/**
 * What can be done about a piece of saved work, from Offline work or from the
 * list it was typed on (.docs/023).
 */

/** A piece's state as a status pill says it. */
export function pieceStatus(piece: Piece): 'NOT_SENT' | 'REFUSED' | 'SENT' {
  return piece.state === 'refused' ? 'REFUSED' : piece.state === 'sent' ? 'SENT' : 'NOT_SENT';
}

/** Whether a refused piece can be loaded back into its form to be put right. */
export function canFix(piece: Piece): boolean {
  const op = piece.refused;
  return op !== null && (op.form !== undefined || op.lane === 'reference');
}

/** Loads refused work back into the form it was typed on. */
export function fixPiece(piece: Piece): void {
  if (piece.refused) useShellNav.getState().navigate(piece.refused.screen, { fixOperation: piece.refused.id });
}

/** Sends refused work again as it is, for when what was wrong was put right elsewhere. */
export async function sendPieceAgain(piece: Piece): Promise<void> {
  if (piece.refused) await retryWork(piece.refused.id);
}

/** Asks, then throws refused work away with whatever stands on it. Answers whether it went. */
export async function throwPieceAway(piece: Piece): Promise<boolean> {
  const op = piece.refused ?? piece.lead;
  const others = (await standingOn(op.id)).filter((o) => !piece.ops.includes(o));
  const ok = await confirmAction({
    title: 'Throw this work away?',
    message:
      `"${piece.lead.describes}" will be removed from this PC. The server will never see it, and it cannot be got back.` +
      (others.length > 0 ? ` These go with it: ${others.map((o) => o.describes).join('; ')}.` : ''),
    confirmLabel: 'Throw it away',
    cancelLabel: 'Keep it',
    danger: true,
  });
  if (!ok) return false;
  await takeBack(op.id);
  return true;
}

